import React, { useState } from "react";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import { useState as useStateForZoom } from "react";
import { colors } from "../../../utils/colors";

interface ImgDisplayProps {
  path: string;
  height: number;
  zoomPath?: string;
}

const imageCache = new Map<string, boolean>();

export const ImgDisplay: React.FC<ImgDisplayProps> = React.memo(
  ({ path, height, zoomPath }) => {
    const [imageExists, setImageExists] = useState<boolean | null>(null);
    const [isZoomed, setIsZoomed] = useStateForZoom(false);

    React.useEffect(() => {
      if (!path || path === "") {
        setImageExists(false);
        return;
      }

      if (imageCache.has(path)) {
        setImageExists(imageCache.get(path)!);
        return;
      }

      setImageExists(null);

      const testImage = new Image();
      testImage.onload = () => {
        imageCache.set(path, true);
        setImageExists(true);
      };
      testImage.onerror = () => {
        console.log("Image does not exist:", path);
        imageCache.set(path, false);
        setImageExists(false);
      };
      testImage.src = path;

      return () => {
        testImage.onload = null;
        testImage.onerror = null;
      };
    }, [path]);

    if (imageExists === null) {
      return (
        <Box
          sx={{
            width: "170px",
            height: height,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: colors.background,
            border: `1px solid ${colors.secondaryText}`,
            borderRadius: "4px",
            color: colors.secondaryText,
            fontSize: "14px",
            textAlign: "center",
          }}
        >
          Loading...
        </Box>
      );
    }

    if (!imageExists) {
      return (
        <Box
          sx={{
            width: "170px",
            height: "auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: colors.background,
            border: `1px solid ${colors.secondaryText}`,
            borderRadius: "4px",
            color: colors.secondaryText,
            fontSize: "14px",
            textAlign: "center",
          }}
        >
          Image not available
        </Box>
      );
    }

    return (
      <>
        <img
          alt="Vehicle"
          src={path}
          width="170px"
          style={{
            objectFit: "fill",
            cursor: "pointer",
            borderRadius: "4px",
          }}
          onClick={(e) => {
            e.stopPropagation();
            setIsZoomed(true);
          }}
        />
        <Dialog
          open={isZoomed}
          onClose={() => setIsZoomed(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: "transparent",
              boxShadow: "none",
              maxHeight: "90vh",
              borderRadius: "0px",
              overflow: "visible",
              position: "relative",
            },
          }}
          BackdropProps={{
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.4)",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
              width: "100%",
              maxWidth: "58vw",
              height: "fit-content",
              padding: 3,
            }}
          >
            <img
              alt="Vehicle (Zoomed)"
              src={zoomPath || path}
              style={{
                width: "100%",
                height: "auto",
                maxHeight: "calc(85vh - 96px)",
                objectFit: "contain",
                borderRadius: "0px",
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
              }}
            />
          </Box>

          <IconButton
            onClick={() => setIsZoomed(false)}
            sx={{
              position: "absolute",
              top: 40,
              right: 40,
              color: "white",
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              borderRadius: "50%",
              width: 48,
              height: 48,
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
              transition: "all 0.2s ease-in-out",
              zIndex: 1,
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.9)",
                transform: "scale(1.05)",
                boxShadow: "0 6px 16px rgba(0, 0, 0, 0.4)",
              },
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6 6L18 18M18 6L6 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </IconButton>
        </Dialog>
      </>
    );
  }
);
